// src/pages/Simulator.tsx
import { useEffect, useMemo, useState } from "react";
import { useSession } from "@supabase/auth-helpers-react";
import { supabase } from "@/lib/supabase";
import QuickLook from "@/components/widgets/QuickLook";
import { EQUITIES } from "@/components/widgets/symbols";

type Position = { id: string; user_id: string; symbol: string; qty: number; avg_price: number };
type Order = { id: string; symbol: string; side: "buy" | "sell"; qty: number; price: number | null; created_at: string };
type Account = { user_id: string; cash: number };

export default function SimulatorPage() {
  const session = useSession();

  const [account, setAccount] = useState<Account | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [symbol, setSymbol] = useState("AAPL");
  const [qty, setQty] = useState<number>(1);
  const [placing, setPlacing] = useState(false);

  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [loadingPrice, setLoadingPrice] = useState(false);

  const equityOptions = EQUITIES.map((it: any) =>
    typeof it === "string" ? { value: it, label: it } : { value: it.symbol, label: it.label ?? it.symbol }
  );

  const total = useMemo(() => (currentPrice ? currentPrice * qty : 0), [currentPrice, qty]);

  // Ensure account + load data
  useEffect(() => {
    if (!session) return;
    (async () => {
      await supabase.rpc("ensure_paper_account");
      await Promise.all([loadAccount(), loadPositions(), loadOrders()]);
    })();
  }, [session?.access_token]);

  async function loadAccount() {
    const { data } = await supabase.from("paper_accounts").select("*").single();
    if (data) setAccount(data as Account);
  }
  async function loadPositions() {
    const { data } = await supabase.from("positions").select("*").order("symbol");
    setPositions((data || []) as Position[]);
  }
  async function loadOrders() {
    const { data } = await supabase
      .from("orders")
      .select("id,symbol,side,qty,price,created_at")
      .order("created_at", { ascending: false });
    setOrders((data || []) as Order[]);
  }

  // Quote fetcher
async function fetchPrice(sym: string) {
  setLoadingPrice(true);
  try {
    const url = `/api/quote?symbol=${encodeURIComponent(sym)}&_t=${Date.now()}`;
    const r = await fetch(url, { cache: "no-store" });
    const j = await r.json().catch(() => ({}));
    setCurrentPrice(typeof j?.price === "number" ? j.price : null);
  } catch {
    setCurrentPrice(null);
  } finally {
    setLoadingPrice(false);
  }
}

  // Fetch price when symbol changes (and on mount)
  useEffect(() => {
    fetchPrice(symbol);
  }, [symbol]);

  // Quick Look tab click → update symbol (and triggers fetch)
  function handleSelectFromQuickLook(sym: string) {
    setSymbol(sym);
  }

  async function place(side: "buy" | "sell") {
    if (!session) return alert("Please sign in");
    if (!symbol || !qty || qty < 1) return alert("Enter a valid quantity.");

    setPlacing(true);
    try {
      const r = await fetch("/api/place-order", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ symbol, side, qty }),
      });
      const txt = await r.text();
      const j = txt ? JSON.parse(txt) : null;
      if (!r.ok) throw new Error(j?.error || j?.message || "Order failed");

      await Promise.all([loadAccount(), loadPositions(), loadOrders()]);
    } catch (e: any) {
      alert(e?.message ?? "Order failed.");
    } finally {
      setPlacing(false);
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Paper Trading Simulator</h1>

      {/* Quick Look */}
      <div className="max-w-5xl">
        <QuickLook onSelect={handleSelectFromQuickLook} />
      </div>

      {/* Order ticket */}
      <div className="space-y-3 border rounded p-4 bg-white shadow max-w-3xl">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <select className="border rounded px-2 py-1" value={symbol} onChange={(e) => setSymbol(e.target.value)}>
            {equityOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <span className="text-sm">
            Current Balance: <b>{account ? `$${account.cash.toFixed(2)}` : "—"}</b>
          </span>

          <div className="flex gap-2">
            <button disabled={placing} onClick={() => place("buy")} className="bg-green-600 text-white rounded px-3 py-1 disabled:opacity-60">
              {placing ? "Placing..." : "Buy"}
            </button>
            <button disabled={placing} onClick={() => place("sell")} className="bg-red-600 text-white rounded px-3 py-1 disabled:opacity-60">
              Sell
            </button>
          </div>
        </div>

        <div className="text-sm">
          Current Price: <b>{loadingPrice ? "Loading…" : currentPrice != null ? `$${currentPrice.toFixed(2)}` : "—"}</b>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm">Number of shares</label>
          <input
            className="border rounded px-2 py-1 w-24 text-right"
            type="number"
            min={1}
            value={qty}
            onChange={(e) => setQty(Math.max(1, Number(e.target.value || 1)))}
          />
        </div>

        <div className="text-sm">
          Total: <b>{currentPrice != null ? `$${total.toFixed(2)}` : "—"}</b>
        </div>
      </div>

      {/* Positions */}
      <section className="max-w-3xl">
        <h2 className="font-semibold mb-2">My Positions</h2>
        {positions.length === 0 ? (
          <p>No positions yet</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="py-2 text-left">Symbol</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Avg Price</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => (
                <tr key={p.id} className="border-b">
                  <td className="py-2">{p.symbol}</td>
                  <td className="text-right">{p.qty}</td>
                  <td className="text-right">{Number(p.avg_price).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Orders */}
      <section className="max-w-3xl">
        <h2 className="font-semibold mb-2">Recent Orders</h2>
        {orders.length === 0 ? (
          <p>No orders yet</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="py-2 text-left">Time</th>
                <th>Side</th>
                <th>Symbol</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Price</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b">
                  <td className="py-2">{new Date(o.created_at).toLocaleString()}</td>
                  <td className={o.side === "buy" ? "text-green-600" : "text-red-600"}>{o.side.toUpperCase()}</td>
                  <td>{o.symbol}</td>
                  <td className="text-right">{o.qty}</td>
                  <td className="text-right">{o.price ? Number(o.price).toFixed(2) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
